import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiUser, FiSearch, FiMail } from 'react-icons/fi';
import { MdPrecisionManufacturing, MdPeople, MdAutorenew, MdAccessTime } from 'react-icons/md';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addDoc, collection, DocumentReference, onSnapshot } from 'firebase/firestore';
import { db } from '../../utils/firebase/config';
import { signInAsAnonymous } from '../../utils/firebase';

const invitations = [
  {
    id: 1,
    company: 'Google',
    position: 'Frontend Developer',
    time: 'Just now',
    details: 'Recruiter will contact you soon to schedule a call.',
    interview: 'Interview: 10:00, Tue',
    color: 'bg-blue-100 border-blue-300',
    icon: <FiMail className="text-blue-500" size={28} />,
  },
  {
    id: 2,
    company: 'Amazon',
    position: 'Backend Engineer',
    time: '2 min ago',
    details: 'Interview link will be sent to your email within 24 hours.',
    interview: 'Interview: 16:30, Fri',
    color: 'bg-green-100 border-green-300',
    icon: <FiMail className="text-green-500" size={28} />,
  },
  {
    id: 3,
    company: 'Spotify',
    position: 'Product Designer',
    time: '5 min ago',
    details: 'Interview scheduled for 14:00, Wednesday.',
    interview: 'Interview: 14:00, Wed',
    color: 'bg-yellow-100 border-yellow-300',
    icon: <FiMail className="text-yellow-500" size={28} />,
  },
];

export const GetOffers: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-black">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-28 flex flex-col md:flex-row items-center md:gap-2">
        {/* Left text */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="text-left max-w-2xl w-full"
        >
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight text-gray-900 drop-shadow-sm">
            Skip the Job Hunt.<br />
            Get Straight to the Interview.
          </h1>
          <p className="text-2xl text-gray-600 mb-10 max-w-xl font-medium">
            We deliver guaranteed interviews based on your target role, industry, and salary expectations.
          </p>
          <button 
            onClick={() => window.location.href = '/getoffers/funnel'}
            className="bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500 shadow-lg text-white font-bold py-4 px-10 rounded-full text-xl transition-all duration-200"
          >
            Get My Guaranteed Interviews
          </button>
          {/* Avatars, rating and caption block */}
          <div className="flex flex-col items-start w-full mt-6">
            <div className="flex items-center mb-2">
              {/* Fake avatars */}
              <div className="flex -space-x-3">
                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="user1" className="w-9 h-9 rounded-full border-2 border-white shadow" />
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="user2" className="w-9 h-9 rounded-full border-2 border-white shadow" />
                <img src="https://randomuser.me/api/portraits/men/65.jpg" alt="user3" className="w-9 h-9 rounded-full border-2 border-white shadow" />
                <img src="https://randomuser.me/api/portraits/women/12.jpg" alt="user4" className="w-9 h-9 rounded-full border-2 border-white shadow" />
                <img src="https://randomuser.me/api/portraits/men/18.jpg" alt="user5" className="w-9 h-9 rounded-full border-2 border-white shadow" />
              </div>
              {/* Rating */}
              <div className="flex items-center ml-4 bg-orange-50 px-3 py-1 rounded-full text-orange-600 font-bold text-sm shadow">
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" className="w-5 h-5 mr-1 text-orange-400"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z"/></svg>
                4.9/5
              </div>
            </div>
            <div className="text-xs text-gray-500 text-left max-w-xs">
              Over <span className="font-bold text-gray-900">1122</span> people got a job in less than a week just by buying an interview for their resume
            </div>
          </div>
        </motion.div>
        {/* Right side email animation */}
        <div className="relative flex flex-col items-start justify-center w-[400px] md:w-[420px] min-w-[260px] md:ml-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full bg-white border border-slate-200 rounded-3xl shadow-2xl p-0 flex flex-col overflow-hidden mb-6"
            style={{ minHeight: 340 }}
          >
            {/* Mini interface header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
              <div className="font-extrabold text-lg flex items-center gap-2">
                <span className="text-orange-500 font-extrabold tracking-tight">hiiroo</span>
                <span className="text-gray-700 font-semibold">inbox</span>
              </div>
              <span className="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-1 rounded-full">3 new</span>
            </div>
            <div className="flex-1 flex flex-col gap-3 px-4 py-4">
              {invitations.map((invite, idx) => (
                <motion.div
                  key={invite.id}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + idx * 0.5, duration: 0.7, type: 'spring' }}
                  className={`flex items-center gap-4 bg-white border border-slate-100 rounded-xl px-5 py-4 shadow-sm relative w-full`}
                  style={{ zIndex: 10 - idx }}
                >
                  {/* Avatar of the company */}
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 ${invite.color} shadow-sm`}>
                    <img
                      src={`https://logo.clearbit.com/${invite.company.toLowerCase()}.com`}
                      alt={invite.company}
                      className="w-7 h-7 object-contain rounded-full"
                      onError={e => (e.currentTarget.style.display = 'none')}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{invite.company}</div>
                    <div className="text-gray-500 text-sm truncate">{invite.position}</div>
                    <div className="text-xs text-gray-400 mt-1">{invite.time}</div>
                    <div className="text-xs text-gray-500 mt-1 truncate">{invite.details}</div>
                  </div>
                  <div className="ml-2 text-xs text-orange-600 font-bold whitespace-nowrap">
                    {invite.interview}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 text-xs text-gray-400 text-center">
              This is how your interview invitations will look in your inbox
            </div>
          </motion.div>
        </div>
      </section>

      {/* Logos Marquee Section */}
      <section className="w-full bg-white">
        <h3 className="text-center text-xl md:text-2xl font-semibold text-gray-800 mb-4 tracking-tight">Trusted by professionals from top US and global companies</h3>
        {/* Inline marquee animation if нет в Tailwind */}
        <style>{`
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-100%); }
}
.animate-marquee {
  animation: marquee 40s linear infinite;
}
`}</style>
        <div className="overflow-hidden py-6">
          <div className="relative w-full">
            <div className="flex items-center gap-12 animate-marquee whitespace-nowrap" style={{ animation: 'marquee 40s linear infinite' }}>
              {/* Логотипы — расширенный список */}
              <img src="https://logo.clearbit.com/google.com" alt="Google" className="h-10 transition" />
              <img src="https://logo.clearbit.com/amazon.com" alt="Amazon" className="h-10 transition" />
              <img src="https://logo.clearbit.com/meta.com" alt="Meta" className="h-10 transition" />
              <img src="https://logo.clearbit.com/microsoft.com" alt="Microsoft" className="h-10 transition" />
              <img src="https://logo.clearbit.com/apple.com" alt="Apple" className="h-10 transition" />
              <img src="https://logo.clearbit.com/netflix.com" alt="Netflix" className="h-10 transition" />
              <img src="https://logo.clearbit.com/stripe.com" alt="Stripe" className="h-10 transition" />
              <img src="https://logo.clearbit.com/airbnb.com" alt="Airbnb" className="h-10 transition" />
              <img src="https://logo.clearbit.com/uber.com" alt="Uber" className="h-10 transition" />
              <img src="https://logo.clearbit.com/tesla.com" alt="Tesla" className="h-10 transition" />
              <img src="https://logo.clearbit.com/ibm.com" alt="IBM" className="h-10 transition" />
              <img src="https://logo.clearbit.com/intel.com" alt="Intel" className="h-10 transition" />
              <img src="https://logo.clearbit.com/salesforce.com" alt="Salesforce" className="h-10 transition" />
              <img src="https://logo.clearbit.com/adobe.com" alt="Adobe" className="h-10 transition" />
              <img src="https://logo.clearbit.com/oracle.com" alt="Oracle" className="h-10 transition" />
              <img src="https://logo.clearbit.com/jpmorganchase.com" alt="JPMorgan" className="h-10 transition" />
              <img src="https://logo.clearbit.com/goldmansachs.com" alt="Goldman Sachs" className="h-10 transition" />
              <img src="https://logo.clearbit.com/twitter.com" alt="Twitter" className="h-10 transition" />
              <img src="https://logo.clearbit.com/youtube.com" alt="YouTube" className="h-10 transition" />
              <img src="https://logo.clearbit.com/pinterest.com" alt="Pinterest" className="h-10 transition" />
              <img src="https://logo.clearbit.com/snap.com" alt="Snap" className="h-10 transition" />
              <img src="https://logo.clearbit.com/shopify.com" alt="Shopify" className="h-10 transition" />
              <img src="https://logo.clearbit.com/slack.com" alt="Slack" className="h-10 transition" />
              <img src="https://logo.clearbit.com/zoom.us" alt="Zoom" className="h-10 transition" />
              <img src="https://logo.clearbit.com/booking.com" alt="Booking.com" className="h-10 transition" />
              <img src="https://logo.clearbit.com/expedia.com" alt="Expedia" className="h-10 transition" />
              <img src="https://logo.clearbit.com/spotify.com" alt="Spotify" className="h-10 transition" />
              <img src="https://logo.clearbit.com/dropbox.com" alt="Dropbox" className="h-10 transition" />
              <img src="https://logo.clearbit.com/atlassian.com" alt="Atlassian" className="h-10 transition" />
              <img src="https://logo.clearbit.com/coinbase.com" alt="Coinbase" className="h-10 transition" />
              <img src="https://logo.clearbit.com/squareup.com" alt="Square" className="h-10 transition" />
              <img src="https://logo.clearbit.com/palantir.com" alt="Palantir" className="h-10 transition" />
              <img src="https://logo.clearbit.com/zoominfo.com" alt="ZoomInfo" className="h-10 transition" />
              <img src="https://logo.clearbit.com/robinhood.com" alt="Robinhood" className="h-10 transition" />
              <img src="https://logo.clearbit.com/lyft.com" alt="Lyft" className="h-10 transition" />
              <img src="https://logo.clearbit.com/instacart.com" alt="Instacart" className="h-10 transition" />
              <img src="https://logo.clearbit.com/target.com" alt="Target" className="h-10 transition" />
              <img src="https://logo.clearbit.com/walmart.com" alt="Walmart" className="h-10 transition" />
              <img src="https://logo.clearbit.com/cisco.com" alt="Cisco" className="h-10 transition" />
              <img src="https://logo.clearbit.com/pepsico.com" alt="PepsiCo" className="h-10 transition" />
              <img src="https://logo.clearbit.com/coca-cola.com" alt="Coca-Cola" className="h-10 transition" />
              <img src="https://logo.clearbit.com/nike.com" alt="Nike" className="h-10 transition" />
              <img src="https://logo.clearbit.com/starbucks.com" alt="Starbucks" className="h-10 transition" />
              {/* Дублируем для бесконечного эффекта */}
              <img src="https://logo.clearbit.com/google.com" alt="Google" className="h-10 transition" />
              <img src="https://logo.clearbit.com/amazon.com" alt="Amazon" className="h-10 transition" />
              <img src="https://logo.clearbit.com/meta.com" alt="Meta" className="h-10 transition" />
              <img src="https://logo.clearbit.com/microsoft.com" alt="Microsoft" className="h-10 transition" />
              <img src="https://logo.clearbit.com/apple.com" alt="Apple" className="h-10 transition" />
              <img src="https://logo.clearbit.com/netflix.com" alt="Netflix" className="h-10 transition" />
              <img src="https://logo.clearbit.com/stripe.com" alt="Stripe" className="h-10 transition" />
              <img src="https://logo.clearbit.com/airbnb.com" alt="Airbnb" className="h-10 transition" />
              <img src="https://logo.clearbit.com/uber.com" alt="Uber" className="h-10 transition" />
              <img src="https://logo.clearbit.com/tesla.com" alt="Tesla" className="h-10 transition" />
              <img src="https://logo.clearbit.com/ibm.com" alt="IBM" className="h-10 transition" />
              <img src="https://logo.clearbit.com/intel.com" alt="Intel" className="h-10 transition" />
              <img src="https://logo.clearbit.com/salesforce.com" alt="Salesforce" className="h-10 transition" />
              <img src="https://logo.clearbit.com/adobe.com" alt="Adobe" className="h-10 transition" />
              <img src="https://logo.clearbit.com/oracle.com" alt="Oracle" className="h-10 transition" />
              <img src="https://logo.clearbit.com/jpmorganchase.com" alt="JPMorgan" className="h-10 transition" />
              <img src="https://logo.clearbit.com/goldmansachs.com" alt="Goldman Sachs" className="h-10 transition" />
            </div>
          </div>
        </div>
      </section>

      {/* Customer Stories Section */}
      <section className="container mx-auto px-4 py-24">
        <div className="mb-4 flex flex-col items-center">
          <span className="inline-block mb-2 px-4 py-1 rounded-full bg-purple-50 text-purple-600 font-semibold tracking-wide text-sm shadow-sm">Customer Stories</span>
          <h2 className="text-4xl font-extrabold text-gray-900 mb-2 text-center">Success Stories from Our Clients</h2>
          <p className="text-lg text-gray-500 mb-8 max-w-2xl text-center">Discover how our service has helped professionals from leading companies land their dream jobs. Read their success stories and learn how we can help you achieve your career goals.</p>
        </div>
        <CustomerStories />
      </section>

      {/* How it Works Section */}
      <section className="container mx-auto px-4 py-24">
        <div className="flex flex-col items-center mb-12">
          <h2 className="text-4xl font-extrabold mb-2 text-gray-900 text-center">How It Works</h2>
          <p className="text-lg text-gray-500 mb-8 max-w-xl text-center">Our streamlined process saves you 40+ hours per week by automating the entire job search and application process.</p>
        </div>
        <div className="flex flex-col md:flex-row gap-12 items-center">
          {/* Левая часть — шаги */}
          <div className="flex-1 space-y-8 relative">
            {/* Соединительные линии */}
            <div className="absolute left-[1.25rem] top-[3.5rem] bottom-[3.5rem] w-0.5 bg-gradient-to-b from-blue-100 via-green-100 to-teal-100"></div>
            
            <div className="flex items-start gap-4 relative">
              <div className="rounded-full p-3 bg-blue-100 z-10">
                <FiUser className="text-blue-500" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Profile & Preferences Setup</h3>
                <p className="text-gray-500">
                  Upload your resume and specify your target roles, salary expectations, and preferred companies. Our AI analyzes your profile to create a personalized matching strategy.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 relative">
              <div className="rounded-full p-3 bg-green-100 z-10">
                <FiSearch className="text-green-500" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Smart Job Matching</h3>
                <p className="text-gray-500">
                  Our AI scans 100,000+ job boards and company career pages daily, identifying positions that match your profile with 95% accuracy. No more manual job searching.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 relative">
              <div className="rounded-full p-3 bg-yellow-100 z-10">
                <FiMail className="text-yellow-500" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Automated Applications</h3>
                <p className="text-gray-500">
                  We automatically fill out applications, customize cover letters, and submit your profile to matching positions. Each application is optimized for ATS systems.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 relative">
              <div className="rounded-full p-3 bg-purple-100 z-10">
                <MdPeople className="text-purple-500" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Recruiter Network Activation</h3>
                <p className="text-gray-500">
                  Your profile is shared with our network of 500+ recruiters and hiring managers. We leverage internal referrals to get your application prioritized.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 relative">
              <div className="rounded-full p-3 bg-pink-100 z-10">
                <MdAutorenew className="text-pink-500" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Interview Scheduling</h3>
                <p className="text-gray-500">
                  When companies express interest, we handle all communication, schedule interviews at your convenience, and ensure you're fully prepared with company-specific insights.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 relative">
              <div className="rounded-full p-3 bg-teal-100 z-10">
                <MdAccessTime className="text-teal-500" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Progress Tracking</h3>
                <p className="text-gray-500">
                  Monitor your application status, interview schedule, and feedback in real-time. Get detailed analytics on your job search performance and success rate.
                </p>
              </div>
            </div>
          </div>

          {/* Правая часть — объединённый интерфейс с календарём */}
          <div className="flex-1 flex flex-col items-center gap-0 relative min-h-[520px] mt-12">
            <UnifiedInterviewInterface />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-24">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-4xl font-extrabold text-center mb-16 text-gray-900"
        >Why Choose Us?</motion.h2>
        <div className="grid md:grid-cols-2 gap-10">
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center border border-slate-100 transition-all duration-200 hover:shadow-2xl"
          >
            <div className="mb-6 bg-purple-100 rounded-2xl p-5 flex items-center justify-center shadow-md">
              <MdPrecisionManufacturing className="text-purple-500" size={44} />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-center text-gray-900">Save Hours Weekly</h3>
            <p className="text-gray-500 text-center text-lg">
              No more endless job search. Get real interview invites, fast.
            </p>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center border border-slate-100 transition-all duration-200 hover:shadow-2xl"
          >
            <div className="mb-6 bg-pink-100 rounded-2xl p-5 flex items-center justify-center shadow-md">
              <MdPeople className="text-pink-500" size={44} />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-center text-gray-900">Get Noticed Instantly</h3>
            <p className="text-gray-500 text-center text-lg">
              Top companies see your profile first—skip the filters.
            </p>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center border border-slate-100 transition-all duration-200 hover:shadow-2xl"
          >
            <div className="mb-6 bg-teal-100 rounded-2xl p-5 flex items-center justify-center shadow-md">
              <MdAutorenew className="text-teal-500" size={44} />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-center text-gray-900">Full Control & Clarity</h3>
            <p className="text-gray-500 text-center text-lg">
              Track every step and interview in one place.
            </p>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center border border-slate-100 transition-all duration-200 hover:shadow-2xl"
          >
            <div className="mb-6 bg-orange-100 rounded-2xl p-5 flex items-center justify-center shadow-md">
              <MdAccessTime className="text-orange-500" size={44} />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-center text-gray-900">Start Faster</h3>
            <p className="text-gray-500 text-center text-lg">
              Land interviews and jobs in days, not months.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-4xl font-extrabold mb-6 text-gray-900">Ready to Get More Interviews?</h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto font-medium">
            Join thousands of professionals who have already landed interviews at leading companies with our help. Sign up now and take the next step in your career journey!
          </p>
          <button 
            onClick={() => window.location.href = '/getoffers/funnel'}
            className="bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500 shadow-lg text-white font-bold py-4 px-10 rounded-full text-xl transition-all duration-200"
          >
            Get My Guaranteed Interviews
          </button>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 py-24">
        <h2 className="text-4xl font-extrabold text-center mb-10 text-gray-900">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto divide-y divide-gray-100 rounded-2xl bg-white shadow-xl">
          {[
            {
              q: "How does Hiiroo guarantee interviews?",
              a: "We leverage AI-driven job matching, direct recruiter outreach, and automated applications to ensure your profile is seen by decision-makers. If you don't receive interview invitations within the guaranteed period, we'll keep working with you for free until you do."
            },
            {
              q: "What makes Hiiroo different from traditional job boards or agencies?",
              a: "Unlike job boards, we don't just list vacancies — we actively apply for you, optimize your resume for each position, and connect you directly with recruiters. Our process is fully automated and personalized, saving you 40+ hours per week."
            },
            {
              q: "What do I need to get started?",
              a: "Just upload your resume and tell us your target roles, industries, and salary expectations. Our platform will handle the rest — from job matching to application submission and interview scheduling."
            },
            {
              q: "How do you optimize my resume and applications?",
              a: "Our AI analyzes each job description and tailors your resume and cover letter to maximize your chances of passing ATS filters and impressing recruiters. You can review and approve all changes before they're sent."
            },
            {
              q: "Will I know where my applications are sent?",
              a: "Absolutely! You get a personal dashboard to track every application, see which companies have viewed your profile, and monitor your interview invitations in real time."
            },
            {
              q: "Is my data and resume safe with Hiiroo?",
              a: "Yes. We use industry-standard encryption and never share your data with third parties without your consent. Your privacy and security are our top priorities."
            },
            {
              q: "Can I use Hiiroo if I'm looking for remote jobs?",
              a: "Absolutely! Hiiroo specializes in remote job opportunities. We focus exclusively on remote positions with top US and global companies. Just tell us your preferences, and we'll match you with the best remote roles available."
            },
            {
              q: "What happens after I get an interview invitation?",
              a: "We'll help you prepare with company-specific insights, interview coaching, and follow-up strategies. Our goal is not just to get you interviews, but to help you land the offer."
            },
            {
              q: "How fast can I expect results?",
              a: "Most users receive their first interview invitations within 1-3 weeks, depending on their profile and market demand. We keep you updated every step of the way."
            },
            {
              q: "What if I have more questions or need support?",
              a: "Our support team and career experts are always here to help. You can reach out via chat or email anytime for personalized assistance."
            }
          ].map((item, i) => (
            <details key={i} className="group py-6 px-6 cursor-pointer">
              <summary className="flex items-center justify-between text-lg font-semibold text-gray-900 group-open:text-orange-600 transition">
                {item.q}
                <span className="ml-2 text-orange-500">{groupOpenIcon}</span>
              </summary>
              <div className="mt-3 text-gray-600 text-base leading-relaxed">{item.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Заключительный CTA-блок */}
      <section className="container mx-auto px-4 py-24">
        <div className="max-w-2xl mx-auto text-center bg-gradient-to-br from-orange-50 to-purple-50 rounded-3xl shadow-2xl p-12">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900">Your Next Interview Awaits</h2>
          <p className="text-xl text-gray-700 mb-10 font-medium">
            Don't wait for opportunities—create them. Take the first step towards your dream job today. The fastest way to get hired is to start now.
          </p>
          <button
            onClick={() => window.location.href = '/getoffers/funnel'}
            className="bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500 shadow-lg text-white font-bold py-4 px-10 rounded-full text-xl transition-all duration-200"
          >
            Get My Guaranteed Interviews
          </button>
        </div>
      </section>
    </div>
  );
};

function UnifiedInterviewInterface() {
  // Пример событий для календаря
  const events = [
    { day: 7, time: '10:00', company: 'Google', color: 'bg-blue-500' },
    { day: 10, time: '16:30', company: 'Amazon', color: 'bg-green-500' },
    { day: 14, time: '14:00', company: 'Spotify', color: 'bg-yellow-500' },
    { day: 18, time: '11:00', company: 'Meta', color: 'bg-purple-500' },
  ];
  // Календарь на текущий месяц (7x5)
  const days = Array.from({ length: 35 }, (_, i) => i + 1);

  return (
    <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 flex flex-col gap-8 items-center border border-slate-100">
      {/* Заголовок */}
      <div className="flex items-center justify-between w-full mb-4">
        <div className="font-extrabold text-lg flex items-center gap-2">
          <img src="https://ssl.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_24_2x.png" alt="Google Calendar" className="w-7 h-7" />
          <span className="text-gray-700 font-semibold">Google Calendar <span className='text-gray-400 font-normal'>x Hiiroo</span></span>
        </div>
        <span className="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-1 rounded-full">June 2025</span>
      </div>
      {/* Календарь */}
      <div className="w-full grid grid-cols-7 gap-2 mb-6">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
          <div key={d} className="text-xs font-bold text-gray-400 text-center pb-1">{d}</div>
        ))}
        {days.map((day) => {
          const event = events.find(e => e.day === day);
          return (
            <div key={day} className={`relative h-14 flex flex-col items-center justify-center rounded-xl border ${event ? event.color + ' text-white font-bold shadow-lg border-transparent' : 'bg-gray-50 text-gray-700 border-slate-100'}`}>
              <span>{day <= 30 ? day : ''}</span>
              {event && (
                <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-xs ${event.color} bg-opacity-90 shadow`}>
                  {event.company} <span className="font-normal">{event.time}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Статистика и прогресс */}
      <div className="w-full flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex-1 bg-gradient-to-r from-blue-50 to-green-50 rounded-2xl p-4 flex flex-col items-center">
          <div className="text-2xl font-bold text-blue-600 mb-1">8</div>
          <div className="text-xs text-gray-500">New Invites</div>
        </div>
        <div className="flex-1 bg-gradient-to-r from-green-50 to-yellow-50 rounded-2xl p-4 flex flex-col items-center">
          <div className="text-2xl font-bold text-green-600 mb-1">5</div>
          <div className="text-xs text-gray-500">Scheduled</div>
        </div>
        <div className="flex-1 bg-gradient-to-r from-yellow-50 to-purple-50 rounded-2xl p-4 flex flex-col items-center">
          <div className="text-2xl font-bold text-purple-600 mb-1">3</div>
          <div className="text-xs text-gray-500">Completed</div>
        </div>
      </div>
    </div>
  );
}

const stories = [
  {
    name: 'Sarah Chen',
    role: 'Senior Product Designer',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    rating: 5,
    text: "I was skeptical at first, but this service completely transformed my job search. Within two weeks, I had three interviews scheduled with top tech companies. The preparation materials were incredibly helpful, and I landed my dream role at a leading design firm!"
  },
  {
    name: 'Michael Rodriguez',
    role: 'Full Stack Developer',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    rating: 5,
    text: "After months of sending applications with no response, I decided to try this service. The difference was night and day. I received interview invitations from companies I never thought would notice my application. The support team was amazing throughout the process."
  },
  {
    name: 'Emma Thompson',
    role: 'Marketing Director',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    rating: 5,
    text: "The personalized approach made all the difference. They understood exactly what I was looking for and matched me with companies that aligned with my career goals. I'm now working at my dream company with a significant salary increase!"
  },
  {
    name: 'David Kim',
    role: 'Data Scientist',
    avatar: 'https://randomuser.me/api/portraits/men/75.jpg',
    rating: 5,
    text: "I was struggling to break into the tech industry, but this service opened doors I didn't even know existed. The interview coaching was invaluable, and I received multiple offers within weeks. Couldn't be happier with the results!"
  },
  {
    name: 'Lisa Patel',
    role: 'UX Researcher',
    avatar: 'https://randomuser.me/api/portraits/women/90.jpg',
    rating: 5,
    text: "The attention to detail and personalized service exceeded my expectations. They helped me prepare for interviews at top companies, and I landed a role that perfectly matches my skills and career aspirations. Worth every penny!"
  }
];

function CustomerStories() {
  const [index, setIndex] = useState(0);
  const prev = () => setIndex((i) => (i === 0 ? stories.length - 1 : i - 1));
  const next = () => setIndex((i) => (i === stories.length - 1 ? 0 : i + 1));

  return (
    <div className="flex justify-center items-center gap-4 mt-8">
      <button
        onClick={prev}
        className="p-2 rounded-full bg-gray-100 hover:bg-purple-100 transition"
        aria-label="Previous story"
      >
        <ChevronLeft className="w-6 h-6 text-purple-500" />
      </button>
      <div className="w-full max-w-2xl">
        <div className="relative">
          <div className="bg-gray-50 rounded-3xl p-8 shadow flex flex-col items-center text-center min-h-[260px] transition-all duration-300">
            <div className="flex flex-col items-center gap-3 mb-4">
              <img src={stories[index].avatar} alt={stories[index].name} className="w-16 h-16 rounded-full border-4 border-white shadow-lg" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">{stories[index].name}</h3>
                <p className="text-gray-600">{stories[index].role}</p>
              </div>
              <div className="flex gap-1">
                {[...Array(stories[index].rating)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
            <p className="text-gray-600 italic">"{stories[index].text}"</p>
          </div>
        </div>
      </div>
      <button
        onClick={next}
        className="p-2 rounded-full bg-gray-100 hover:bg-purple-100 transition"
        aria-label="Next story"
      >
        <ChevronRight className="w-6 h-6 text-purple-500" />
      </button>
    </div>
  );
}

const groupOpenIcon = (
  <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
); 

function getUserId(): string | null {
  // Если используешь контекст авторизации, замени на свой способ получения userId
  return (window as any).auth?.currentUser?.uid || null;
}

export async function startCheckoutSession(
  price: string,
  mode: 'payment' | 'subscription',
  onStart: (start: unknown) => void,
  onSuccess: (success: DocumentReference) => void,
  onError: (err: unknown) => void
) {
  let userId = getUserId();
  if (!userId) {
    const user = await signInAsAnonymous();
    userId = user.uid;
  }
  addDoc(collection(db, 'customers', userId, 'checkout_sessions'), {
    price,
    mode,
    success_url: window.location.origin,
    cancel_url: window.location.origin + window.location.pathname + '?canceled=true',
    expires_at: Math.floor(Date.now() / 1000) + 60 * 30
  }).then((docRef) => {
    onStart('start');
    onSuccess(docRef);
  }, onError);
} 